import React from "react";
import {
  ActivityIndicator,
  Image,
  Platform,
  StyleSheet,
  Text,
  TextInput,
  TouchableOpacity,
  View,
} from "react-native";

import { RouteProp } from "@react-navigation/native";
import { StackNavigationProp } from "@react-navigation/stack";

import { Camera, CameraType } from "expo-camera";
import { ResizeMode, Video } from "expo-av";
import * as FileSystem from "expo-file-system";
import * as ImagePicker from "expo-image-picker";
import * as VideoThumbnails from "expo-video-thumbnails";
import { VideoThumbnailsResult } from "expo-video-thumbnails/build/VideoThumbnailsTypes.types";

import Button from "../../components/Button.component";
import Loading from "../../components/Loading.component";

import { apiCreateThot, apiFindRootThotByUrl } from "../../api/thots.api";
import { StackNavigatorParam } from "../../models/StackNavigatorParam";
import { ThotType } from "../../models/Thot.type";
import screenStyle from "../../styles/screen.style";
import getVideoCover from "../../libs/videoCover";
import { buildThotRouteParams } from "../../libs/thotRoute";

type Props = {
  navigation: StackNavigationProp<StackNavigatorParam, "NewThot">;
  route: RouteProp<StackNavigatorParam, "NewThot">;
};

type DraftMedia = {
  uri: string;
  type: "video" | "image" | "audio";
  name: string;
  mimeType?: string;
  file?: File | null;
  thumbnailUri?: string | null;
  thumbnailFile?: File | null;
  thumbnailRemote?: string | null;
};

type State = {
  mode: "camera" | "url" | "upload";
  optionTrayOpen: boolean;
  video: { uri: string } | null;
  draftMedia: DraftMedia | null;
  recording: boolean;
  showCamera: boolean;
  noPermission: boolean;
  loading: boolean;
  summaryLoading: boolean;
  summaryRequested: boolean;
  invalid: boolean;
  title: string;
  urlInput: string;
};

const videoEnding = {
  "video/webm;codecs=vp8": "webm",
  "video/webm;codecs=vp9": "webm",
  "video/mp4": "mp4",
};

const WORKFLOW_URL = "http://localhost:8000/workflow/";
const WORKFLOW_ID = "36ba4876-e42f-4481-85f9-61acc2651ce9";
const MAX_WORKFLOW_FILE_BYTES = 50 * 1024 * 1024;
const IMAGE_EXTENSIONS = ["jpg", "jpeg", "png", "gif", "webp", "bmp", "heic", "heif"];
const AUDIO_EXTENSIONS = ["mp3", "wav", "m4a", "aac", "ogg"];
const VIDEO_EXTENSIONS = ["mp4", "mov", "qt", "webm", "m4v", "avi", "mkv"];

export default class NewThot extends React.Component<Props, State> {
  state: State = {
    mode: "camera",
    optionTrayOpen: false,
    video: null,
    draftMedia: null,
    recording: false,
    showCamera: false,
    noPermission: false,
    loading: false,
    summaryLoading: false,
    summaryRequested: false,
    invalid: false,
    title: "",
    urlInput: "",
  };

  cam: Camera | null = null;
  title: string = "";
  webRecorder: MediaRecorder | null = null;
  mimeType: string | null;
  webFile: File | null = null;

  constructor(props) {
    super(props);
    this.mimeType = this.getSupportedMimeType();
  }

  componentDidMount() {
    this._showCamera();
  }

  componentDidUpdate(prevProps: Props, prevState: State) {
    const prevMediaUri = prevState.draftMedia?.uri || prevState.video?.uri || "";
    const nextMediaUri = this.state.draftMedia?.uri || this.state.video?.uri || "";

    if (!prevMediaUri && nextMediaUri && !this.state.summaryRequested && this.state.mode !== "url") {
      this.setState({ summaryRequested: true }, () => {
        this._autoSummary();
      });
    }
  }

  _goBack = () => {
    if (this.state.mode !== "camera" || this.state.video || this.state.draftMedia) {
      this._resetToCamera();
      return;
    }

    if (this.props.navigation.canGoBack()) this.props.navigation.goBack();
    else this.props.navigation.navigate("Home");
  };

  _resetToCamera = () => {
    if (this.state.video?.uri && Platform.OS !== "web") FileSystem.deleteAsync(this.state.video.uri).catch(() => {});
    if (this.state.draftMedia?.thumbnailUri && Platform.OS !== "web" && this.state.draftMedia.type === "video") {
      FileSystem.deleteAsync(this.state.draftMedia.thumbnailUri).catch(() => {});
    }

    this.webFile = null;
    this.setState({
      mode: "camera",
      optionTrayOpen: false,
      video: null,
      draftMedia: null,
      recording: false,
      loading: false,
      summaryLoading: false,
      summaryRequested: false,
      invalid: false,
      title: "",
      urlInput: "",
    });
  };

  _openCreatedThot = (thot: ThotType) => {
    this.setState({ loading: false }, () => {
      this.props.route.params.addThot?.(thot);
      this.props.navigation.navigate("Thot", buildThotRouteParams(thot, undefined, undefined, undefined, true));
    });
  };

  getSupportedMimeType = () => {
    if (typeof MediaRecorder === "undefined") return null;
    if (MediaRecorder.isTypeSupported("video/webm;codecs=vp8")) return "video/webm;codecs=vp8";
    if (MediaRecorder.isTypeSupported("video/mp4")) return "video/mp4";
    if (MediaRecorder.isTypeSupported("video/webm;codecs=vp9")) return "video/webm;codecs=vp9";
    return null;
  };

  _getMediaType = (
    mimeType?: string | null,
    nameOrUri?: string | null,
    assetType?: string | null
  ): "video" | "image" | "audio" => {
    const safeMimeType = (mimeType || "").toLowerCase();
    const safeAssetType = (assetType || "").toLowerCase();
    const lowerName = (nameOrUri || "").toLowerCase();
    const extension = lowerName.includes(".") ? lowerName.split(".").pop() || "" : "";

    if (safeMimeType.startsWith("image/") || safeAssetType === "image" || IMAGE_EXTENSIONS.includes(extension)) return "image";
    if (safeMimeType.startsWith("audio/") || safeAssetType === "audio" || AUDIO_EXTENSIONS.includes(extension)) return "audio";
    if (safeMimeType.startsWith("video/") || safeAssetType === "video" || VIDEO_EXTENSIONS.includes(extension)) return "video";

    return "video";
  };

  _showCamera = async (): Promise<void> => {
    this.setState({ noPermission: false, showCamera: false });

    try {
      const cameraPermission = await Camera.requestCameraPermissionsAsync();
      const microphonePermission = Camera.requestMicrophonePermissionsAsync
        ? await Camera.requestMicrophonePermissionsAsync()
        : { status: "granted" };
      const granted =
        cameraPermission?.status === "granted" &&
        microphonePermission?.status === "granted";

      if (granted) this.setState({ showCamera: true, noPermission: false });
      else this.setState({ showCamera: false, noPermission: true });
    } catch (_) {
      this.setState({ showCamera: false, noPermission: true });
    }
  };

  _setCamRef = (cam: Camera): void => {
    this.cam = cam;
  };

  _toogleRecord = (): void => {
    if (this.state.recording) this._stopRecord();
    else this._startRecord();
  };

  _stopRecord = (): void => {
    this.setState({ recording: false }, () => {
      if (Platform.OS == "web") {
        if (this.webRecorder) this.webRecorder.stop();
      } else {
        this.cam?.stopRecording();
      }
    });
  };

  _startRecord = async (): Promise<void> => {
    if (!this.cam || this.state.recording) return;

    this.setState({ recording: true }, async () => {
      if (Platform.OS == "web") {
        try {
          const blobsRecorded = [];
          const camera_stream = await navigator.mediaDevices.getUserMedia({ video: true, audio: true });
          this.webRecorder = new MediaRecorder(camera_stream, { mimeType: this.mimeType || undefined });
          this.webRecorder.start();
          this.webRecorder.addEventListener("dataavailable", (e) => blobsRecorded.push(e.data));
          this.webRecorder.addEventListener("stop", () => {
            const name = "thot." + videoEnding[this.mimeType] || "webm";
            this.webFile = new File(blobsRecorded, name, { type: this.webRecorder?.mimeType || "video/webm" });
            const uri = window.URL.createObjectURL(this.webFile);
            this.setState({ video: { uri }, summaryRequested: true }, () => this._autoSummary());
          });
        } catch (e) {
          alert("Error > startRecord: \n" + JSON.stringify(e));
          this.setState({ recording: false });
        }
      } else {
        const video = await this.cam!.recordAsync();
        this.setState({ video, summaryRequested: true }, () => this._autoSummary());
      }
    });
  };

  _onChangeTitle = (title: string): void => {
    this.title = title;
    if (this.state.invalid) this.setState({ invalid: false, title });
    else this.setState({ title });
  };

  _onChangeUrl = (urlInput: string): void => {
    if (this.state.invalid) this.setState({ invalid: false, urlInput });
    else this.setState({ urlInput });
  };

  _fileToDataUrl = async (): Promise<string | null> => {
    const draft = this.state.draftMedia;
    const video = this.state.video;

    if (draft?.file && Platform.OS === "web") {
      if (draft.file.size > MAX_WORKFLOW_FILE_BYTES) return null;
      return await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(String(reader.result || ""));
        reader.onerror = reject;
        reader.readAsDataURL(draft.file!);
      });
    }

    if (this.webFile && Platform.OS === "web") {
      if (this.webFile.size > MAX_WORKFLOW_FILE_BYTES) return null;
      return await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(String(reader.result || ""));
        reader.onerror = reject;
        reader.readAsDataURL(this.webFile!);
      });
    }

    const uri = draft?.uri || video?.uri;
    if (!uri) return null;

    if (Platform.OS !== "web") {
      const info = await FileSystem.getInfoAsync(uri, { size: true });
      if (info.exists && info.size && info.size > MAX_WORKFLOW_FILE_BYTES) return null;
    }

    const mimeType =
      draft?.mimeType ||
      (draft?.type === "image" ? "image/jpeg" : draft?.type === "audio" ? "audio/mpeg" : "video/mp4");

    const base64 = await FileSystem.readAsStringAsync(uri, {
      encoding: FileSystem.EncodingType.Base64,
    });

    return `data:${mimeType};base64,${base64}`;
  };

  _callWorkflow = async (payload: { file?: string; url?: string }) => {
    const res = await fetch(WORKFLOW_URL, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        id: WORKFLOW_ID,
        ...payload,
      }),
    });

    const data = await res.json();
    return {
      title: data?.title || data?.message?.content || "",
      image: data?.image || null,
    };
  };

  _autoSummary = async (): Promise<void> => {
    try {
      this.setState({ summaryLoading: true });

      const file = await this._fileToDataUrl();
      if (!file) {
        const fallbackTitle =
          this.state.draftMedia?.name ||
          this.state.video?.uri?.split("/")?.pop() ||
          this.state.title;
        if (fallbackTitle) {
          this.title = fallbackTitle;
          this.setState({ title: fallbackTitle, invalid: false });
        }
        return;
      }

      const data = await this._callWorkflow({ file });
      const nextTitle = typeof data.title === "string" ? data.title.trim() : "";

      this.title = nextTitle;
      this.setState((prev) => ({
        title: nextTitle || prev.title,
        invalid: false,
        draftMedia: prev.draftMedia
          ? {
              ...prev.draftMedia,
              thumbnailRemote: data.image || prev.draftMedia.thumbnailRemote || null,
            }
          : prev.draftMedia,
      }));
    } catch (_) {
      // silent best effort
    } finally {
      this.setState({ summaryLoading: false });
    }
  };

  _chooseUrlMode = () => {
    this.setState({
      mode: "url",
      optionTrayOpen: false,
      video: null,
      draftMedia: null,
      summaryRequested: false,
      summaryLoading: false,
      invalid: false,
      title: "",
    });
  };

  _chooseUploadMode = async () => {
    this.setState({ optionTrayOpen: false });

    if (Platform.OS === "web") {
      const input = document.createElement("input");
      input.type = "file";
      input.accept = "image/*,video/*,audio/*";
      input.onchange = async () => {
        const file = input.files?.[0];
        if (!file) return;

        const mimeType = file.type || "";
        const type = this._getMediaType(mimeType, file.name, null);
        const uri = window.URL.createObjectURL(file);
        let thumbnailUri: string | null = null;
        let thumbnailFile: File | null = null;

        if (type === "video") {
          const thumbnailBlob = await getVideoCover(uri, "image/jpeg");
          thumbnailFile = new File([thumbnailBlob], "thumbnail.jpg", { type: "image/jpeg" });
          thumbnailUri = window.URL.createObjectURL(thumbnailFile);
        } else if (type === "image") {
          thumbnailUri = uri;
        }

        this.webFile = type === "video" ? file : null;
        this.setState(
          {
            mode: "upload",
            draftMedia: {
              uri,
              type,
              name: file.name,
              mimeType,
              file,
              thumbnailUri,
              thumbnailFile,
              thumbnailRemote: null,
            },
            video: null,
            summaryRequested: true,
            summaryLoading: false,
            invalid: false,
          },
          this._autoSummary
        );
      };
      input.click();
      return;
    }

    const result: any = await ImagePicker.launchImageLibraryAsync({
      mediaTypes: ImagePicker.MediaTypeOptions.All,
      quality: 1,
      allowsEditing: false,
    });

    if (result?.canceled || !result?.assets?.[0]) return;

    const asset = result.assets[0];
    const mimeType = asset.mimeType || "";
    const type = this._getMediaType(mimeType, asset.fileName || asset.uri, asset.type);
    let thumbnailUri: string | null = null;

    if (type === "video") {
      const thumbnail: VideoThumbnailsResult = await VideoThumbnails.getThumbnailAsync(asset.uri, {
        quality: 0.9,
        time: 600,
      });
      thumbnailUri = thumbnail.uri;
    } else {
      thumbnailUri = asset.uri;
    }

    this.setState(
      {
        mode: "upload",
        draftMedia: {
          uri: asset.uri,
          type,
          name: asset.fileName || asset.uri.split("/").pop() || "upload",
          mimeType,
          thumbnailUri,
          thumbnailRemote: null,
        },
        video: null,
        summaryRequested: true,
        summaryLoading: false,
        invalid: false,
      },
      this._autoSummary
    );
  };

  _saveRecordedVideo = async (): Promise<void> => {
    if (this.title.length < 3) return this.setState({ invalid: true });

    const { video } = this.state;
    if (!video?.uri) return;

    this.setState({ loading: true });

    try {
      let thumbnailUri: string | null = null;
      let imageFile: File | null = null;

      if (Platform.OS === "web") {
        const thumbnailBlob: Blob = await getVideoCover(video.uri, "image/jpeg");
        imageFile = new File([thumbnailBlob], "thumbnail.jpg", { type: "image/jpeg" });
        thumbnailUri = window.URL.createObjectURL(imageFile);
      } else {
        const thumbnail: VideoThumbnailsResult = await VideoThumbnails.getThumbnailAsync(video.uri, {
          quality: 0.9,
          time: 600,
        });
        thumbnailUri = thumbnail.uri;
      }

      const created = await apiCreateThot({
        uri: video.uri,
        title: this.title,
        parent: this.props.route.params.parent,
        thumbnail: thumbnailUri || undefined,
        mediaFile: this.webFile || undefined,
        imageFile,
        mediaKind: "video",
      });

      this._openCreatedThot(created);
    } catch (err) {
      alert("Error > saveVideo: \n" + JSON.stringify(err));
      this.setState({ loading: false });
    }
  };

  _saveUrl = async () => {
    const url = this.state.urlInput.trim();
    if (!url) return this.setState({ invalid: true });

    this.setState({ loading: true, invalid: false });

    try {
      if (!this.props.route.params.parent) {
        const existing = await apiFindRootThotByUrl(url);
        if (existing) {
          this.setState({ loading: false });
          this._openCreatedThot(existing);
          return;
        }
      }

      const workflow = await this._callWorkflow({ url });
      const nextTitle = (workflow.title || url).trim();
      const created = await apiCreateThot({
        title: nextTitle,
        parent: this.props.route.params.parent,
        url,
        mediaKind: "url",
        thumbnailRemote: workflow.image || undefined,
      });

      this._openCreatedThot(created);
    } catch (err) {
      alert("Error > saveUrl: \n" + JSON.stringify(err));
      this.setState({ loading: false });
    }
  };

  _saveUploadedMedia = async () => {
    const draft = this.state.draftMedia;
    if (!draft) return;
    if (this.title.length < 3) return this.setState({ invalid: true });

    this.setState({ loading: true, invalid: false });

    try {
      const created = await apiCreateThot({
        uri: Platform.OS === "web" ? undefined : draft.uri,
        title: this.title,
        parent: this.props.route.params.parent,
        thumbnail: draft.thumbnailUri || undefined,
        mediaFile: draft.file || undefined,
        imageFile: draft.thumbnailFile || undefined,
        mediaKind: draft.type,
        mediaName: draft.name,
        mediaType: draft.mimeType,
        thumbnailRemote: draft.thumbnailRemote || undefined,
      });

      this._openCreatedThot(created);
    } catch (err) {
      alert("Error > saveUpload: \n" + JSON.stringify(err));
      this.setState({ loading: false });
    }
  };

  _renderMain = () => {
    const { mode, showCamera, noPermission, recording, video, draftMedia } = this.state;

    if (mode === "camera" && !video) {
      if (!showCamera && !noPermission) {
        return (
          <View style={[screenStyle.container, screenStyle.center]}>
            <ActivityIndicator size="large" color="#000" />
          </View>
        );
      }

      if (noPermission) {
        return (
          <View style={[screenStyle.container, screenStyle.center]}>
            <Text style={styles.noPermissionText}>No permissions</Text>
            <Button title="Give permission" onPress={this._showCamera} />
          </View>
        );
      }

      return (
        <Camera
          ref={this._setCamRef}
          style={styles.camera}
          type={CameraType.front}
        />
      );
    }

    if (mode === "camera" && video) {
      return (
        <Video
          source={{ uri: video.uri }}
          resizeMode={ResizeMode.COVER}
          shouldPlay
          isLooping
          style={styles.mediaPreview}
        />
      );
    }

    if (mode === "upload" && draftMedia) {
      if (draftMedia.type === "image") {
        return <Image source={{ uri: draftMedia.uri }} resizeMode="cover" style={styles.mediaPreview} />;
      }

      return (
        <View style={styles.mediaPreviewWrap}>
          {draftMedia.thumbnailUri ? <Image source={{ uri: draftMedia.thumbnailUri }} resizeMode="cover" style={styles.mediaPreview} /> : null}
          <Video
            source={{ uri: draftMedia.uri }}
            resizeMode={ResizeMode.COVER}
            shouldPlay
            isLooping
            style={draftMedia.type === "audio" ? styles.audioPlayer : styles.mediaPreview}
          />
          {draftMedia.type === "audio" ? <Text style={styles.audioLabel}>audio</Text> : null}
        </View>
      );
    }

    return <View style={[screenStyle.container, styles.urlModeScreen]} />;
  };

  render() {
    const { loading, summaryLoading, mode, optionTrayOpen, recording, video, draftMedia, urlInput } = this.state;
    const canSendUrl = !!urlInput.trim() && !loading;
    const canSendUpload = !!draftMedia && this.title.length >= 3 && !loading;
    const canSendVideo = !!video && this.title.length >= 3 && !loading;
    const centerLabel = mode === "url"
      ? urlInput
      : mode === "upload"
      ? draftMedia?.name || ""
      : recording
      ? "Stop"
      : "capture thought";

    return (
      <View style={[screenStyle.container, styles.container]}>
        <Loading visible={loading} />
        {this._renderMain()}

        {mode === "camera" && !!video ? (
          <View style={styles.formArea}>
            <View style={styles.titleWrap}>
              <TextInput
                style={[screenStyle.input, screenStyle.marginB]}
                placeholder="Titel"
                value={this.state.title}
                onChangeText={this._onChangeTitle}
              />
              {summaryLoading ? <ActivityIndicator size="small" color="#000" style={styles.titleSpinner} /> : null}
            </View>
          </View>
        ) : null}

        <View style={styles.bottomBar}>
          <TouchableOpacity style={styles.bottomCircleButton} onPress={this._goBack}>
            <Text style={styles.bottomCircleButtonIcon}>←</Text>
          </TouchableOpacity>

          {mode === "url" ? (
            <View style={styles.centerFieldWrap}>
              <TextInput
                style={[styles.urlInput, this.state.invalid && styles.invalidInput]}
                placeholder="url"
                value={urlInput}
                onChangeText={this._onChangeUrl}
                autoCapitalize="none"
                autoCorrect={false}
              />
            </View>
          ) : mode === "upload" ? (
            <View style={styles.centerFieldWrap}>
              <View style={styles.fileNamePill}>
                <Text numberOfLines={1} ellipsizeMode="middle">{centerLabel}</Text>
              </View>
            </View>
          ) : (
            <Button
              style={styles.captureButton}
              title={centerLabel}
              onPress={video ? this._resetToCamera : this._toogleRecord}
            />
          )}

          <View style={styles.plusWrap}>
            {optionTrayOpen ? (
              <View style={styles.optionTray}>
                <TouchableOpacity style={styles.optionTag} onPress={this._chooseUrlMode}>
                  <Text>url</Text>
                </TouchableOpacity>
                <TouchableOpacity style={styles.optionTag} onPress={this._chooseUploadMode}>
                  <Text>upload</Text>
                </TouchableOpacity>
              </View>
            ) : null}

            {mode === "camera" && !video ? (
              <TouchableOpacity style={styles.bottomCircleButton} onPress={() => this.setState((prev) => ({ optionTrayOpen: !prev.optionTrayOpen }))}>
                <Text style={styles.bottomCircleButtonIcon}>+</Text>
              </TouchableOpacity>
            ) : (
              <TouchableOpacity
                style={[
                  styles.bottomCircleButton,
                  !((mode === "url" && canSendUrl) || (mode === "upload" && canSendUpload) || (mode === "camera" && canSendVideo)) && styles.bottomPlusButtonDisabled,
                ]}
                onPress={mode === "url" ? this._saveUrl : mode === "upload" ? this._saveUploadedMedia : this._saveRecordedVideo}
                disabled={!((mode === "url" && canSendUrl) || (mode === "upload" && canSendUpload) || (mode === "camera" && canSendVideo))}
              >
                <Text style={styles.bottomCircleButtonIcon}>→</Text>
              </TouchableOpacity>
            )}
          </View>
        </View>
      </View>
    );
  }
}

const styles = StyleSheet.create({
  container: {
    justifyContent: "flex-end",
    paddingBottom: 20,
  },
  camera: {
    ...StyleSheet.absoluteFillObject,
    justifyContent: "flex-end",
    alignItems: "center",
  },
  mediaPreview: {
    ...StyleSheet.absoluteFillObject,
  },
  mediaPreviewWrap: {
    ...StyleSheet.absoluteFillObject,
    backgroundColor: "#000",
    alignItems: "center",
    justifyContent: "center",
  },
  audioPlayer: {
    position: "absolute",
    width: 1,
    height: 1,
    opacity: 0.01,
  },
  audioLabel: {
    color: "#fff",
    fontSize: 18,
    fontWeight: "600",
  },
  urlModeScreen: {
    ...StyleSheet.absoluteFillObject,
    backgroundColor: screenStyle.container.backgroundColor || "#edebea",
  },
  noPermissionText: {
    textAlign: "center",
    marginBottom: 30,
  },
  formArea: {
    flex: 1,
    width: "100%",
    justifyContent: "center",
  },
  titleWrap: {
    width: "100%",
    position: "relative",
  },
  titleSpinner: {
    position: "absolute",
    right: 14,
    top: 12,
  },
  bottomBar: {
    width: "100%",
    flexDirection: "row",
    alignItems: "center",
    paddingHorizontal: screenStyle.container.padding,
    paddingBottom: 20,
  },
  bottomCircleButton: {
    width: 48,
    height: 48,
    borderRadius: 24,
    backgroundColor: "#fff",
    alignItems: "center",
    justifyContent: "center",
  },
  bottomCircleButtonIcon: {
    fontSize: 22,
    color: "#111",
    lineHeight: 22,
    textAlign: "center",
  },
  captureButton: {
    flex: 1,
    width: "auto",
    marginBottom: 0,
    marginHorizontal: 10,
    minHeight: 48,
  },
  centerFieldWrap: {
    flex: 1,
    marginHorizontal: 10,
  },
  urlInput: {
    ...screenStyle.input,
    marginBottom: 0,
    minHeight: 48,
  },
  invalidInput: {
    borderColor: "#f00",
  },
  fileNamePill: {
    minHeight: 48,
    borderRadius: 999,
    backgroundColor: "#fff",
    alignItems: "center",
    justifyContent: "center",
    paddingHorizontal: 20,
  },
  plusWrap: {
    position: "relative",
  },
  optionTray: {
    position: "absolute",
    right: 0,
    bottom: 58,
    flexDirection: "row",
    columnGap: 8,
    ...(Platform.OS === "web"
      ? ({
          transitionProperty: "transform, opacity",
          transitionDuration: "220ms",
          transitionTimingFunction: "ease-out",
        } as any)
      : {}),
  },
  optionTag: {
    height: 28,
    borderRadius: 999,
    backgroundColor: "#fff",
    alignItems: "center",
    justifyContent: "center",
    paddingHorizontal: 12,
  },
  bottomPlusButtonDisabled: {
    opacity: 0.35,
  },
});
